/** Fuso do Brasil — o produto opera só aqui. */
export const BR_TIMEZONE = "America/Sao_Paulo";

/** Offset fixo (sem horário de verão desde 2019). */
const BR_OFFSET = "-03:00";

function brFormatParts(iso: string | Date, options: Intl.DateTimeFormatOptions) {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  return new Intl.DateTimeFormat("pt-BR", {
    ...options,
    timeZone: BR_TIMEZONE,
  }).formatToParts(date);
}

function partValue(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes) {
  return parts.find((p) => p.type === type)?.value ?? "";
}

/** Ex.: `06 de jul` a partir de `YYYY-MM-DD` (data civil em Brasília). */
export function formatBrShortDateYmd(dateYmd: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    timeZone: BR_TIMEZONE,
  }).format(new Date(`${dateYmd.trim()}T12:00:00${BR_OFFSET}`));
}
/** Exibe data/hora no fuso de Brasília. */
export function formatBrDateTime(
  iso: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "long", timeStyle: "short" },
): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", { ...options, timeZone: BR_TIMEZONE }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

/** Ex.: `5 de jul • 19:00` */
export function formatBrShortDateTime(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: BR_TIMEZONE,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const month = partValue(parts, "month").replace(/\.$/, "");
  return `${partValue(parts, "day")} de ${month} • ${partValue(parts, "hour")}:${partValue(parts, "minute")}`;
}

/** Apenas hora `HH:mm` em Brasília. */
export function formatBrTime(iso: string): string {
  return formatBrDateTime(iso, { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Partes de calendário a partir de `YYYY-MM-DD` (sem hora). */
export function splitBrCalendarDateYmd(dateYmd: string | null | undefined) {
  if (!dateYmd?.trim()) return null;
  try {
    const parts = brFormatParts(new Date(`${dateYmd.trim()}T12:00:00${BR_OFFSET}`), {
      day: "2-digit",
      month: "long",
    });
    return {
      day: Number(partValue(parts, "day")),
      month: partValue(parts, "month"),
    };
  } catch {
    return null;
  }
}

export function splitBrEventDate(iso: string | null | undefined) {
  if (!iso) return null;
  try {
    const parts = brFormatParts(iso, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return {
      weekday: partValue(parts, "weekday"),
      day: Number(partValue(parts, "day")),
      month: partValue(parts, "month"),
      year: Number(partValue(parts, "year")),
      time: `${partValue(parts, "hour")}:${partValue(parts, "minute")}`,
    };
  } catch {
    return null;
  }
}

/** Partes detalhadas para hero de jogos (dia da semana, mês por extenso, etc.). */
export function splitBrGameDateDetailed(iso: string) {
  const parts = brFormatParts(iso, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const time = `${partValue(parts, "hour")}:${partValue(parts, "minute")}`;
  return {
    dayOfWeek: partValue(parts, "weekday"),
    day: partValue(parts, "day"),
    month: partValue(parts, "month"),
    year: partValue(parts, "year"),
    time,
    hours: time,
  };
}

/** `YYYY-MM` → `Julho 2026` */
export function formatBrMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split("-").map(Number);
  const label = new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
    timeZone: BR_TIMEZONE,
  }).format(new Date(Date.UTC(year, month - 1, 1)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** Data civil `YYYY-MM-DD` (mês 0-indexado). */
export function ymdToIso(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** Hoje em Brasília. */
export function brNowYmd(): { year: number; monthIndex: number; day: number } {
  const parts = brFormatParts(new Date(), {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return {
    year: Number(partValue(parts, "year")),
    monthIndex: Number(partValue(parts, "month")) - 1,
    day: Number(partValue(parts, "day")),
  };
}

/** ISO → `YYYY-MM-DD` em Brasília (para calendário e filtros). */
export function isoToYmdBr(iso: string | Date): string {
  const parts = brFormatParts(iso, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return `${partValue(parts, "year")}-${partValue(parts, "month")}-${partValue(parts, "day")}`;
}

/** ISO → `{ date: YYYY-MM-DD, time: HH:mm }` em Brasília. */
export function isoToDateAndTimeBr(iso: string | null | undefined): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  try {
    const parts = brFormatParts(iso, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return {
      date: `${partValue(parts, "year")}-${partValue(parts, "month")}-${partValue(parts, "day")}`,
      time: `${partValue(parts, "hour")}:${partValue(parts, "minute")}`,
    };
  } catch {
    return { date: "", time: "" };
  }
}

/** `date` + `time` em Brasília → ISO UTC para a API. */
export function dateAndTimeBrToIso(
  date: string,
  time: string,
  emptyTimeFallback = "00:00",
): string | undefined {
  if (!date) return undefined;
  const clock = (time || "").trim() || emptyTimeFallback;
  const d = new Date(`${date}T${clock}:00${BR_OFFSET}`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Converte ISO (UTC) para valor de `<input type="datetime-local">` em horário de Brasília. */
export function isoToDatetimeLocalBr(iso: string): string {
  if (!iso) return "";
  try {
    const parts = brFormatParts(iso, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${partValue(parts, "year")}-${partValue(parts, "month")}-${partValue(parts, "day")}T${partValue(parts, "hour")}:${partValue(parts, "minute")}`;
  } catch {
    return iso.slice(0, 16);
  }
}

/** `YYYY-MM` do mês atual em Brasília. */
export function brCurrentMonthKey(): string {
  const { year, monthIndex } = brNowYmd();
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
}

/** ISO → `YYYY-MM` em Brasília (ex.: mês do jogo para previsão financeira). */
export function isoToMonthKeyBr(iso: string): string {
  const formatted = new Intl.DateTimeFormat("en-CA", {
    timeZone: BR_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
  return formatted.slice(0, 7);
}

/** `YYYY-MM` de N meses atrás (0 = mês atual). */
export function brMonthKeyOffset(monthsBack = 0): string {
  const { year, monthIndex } = brNowYmd();
  const d = new Date(Date.UTC(year, monthIndex - monthsBack, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Interpreta `datetime-local` como horário de Brasília e retorna ISO UTC para a API. */
export function datetimeLocalBrToIso(localValue: string): string {
  if (!localValue) return "";
  const withSeconds = localValue.length === 16 ? `${localValue}:00` : localValue;
  return new Date(`${withSeconds}${BR_OFFSET}`).toISOString();
}
